import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/index';
import { useDispatch } from 'react-redux';
import { NodeField } from '@components/Flow/Nodes';
import { Alert, Chip, Stack, ToggleButton, ToggleButtonGroup, capitalize } from '@mui/material';
import { Label } from '@common/Label';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { Secrets } from '@common/Autocomplete/Secrets';
import { useSecrets } from '@common/hooks/useSecrets';
import { IntegrationSecret } from '@requests/secrets';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const RequestAuthentication: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { secrets, loading: isSecretsLoading, refetch } = useSecrets();

  const state = command.state;
  if (!state?.auth) return null;

  const enabled = state.auth.enabled;
  const secretName = state.auth.secretName;
  const secretType = state.auth.secretUsage;

  const toggleEnabled = (newVal: string) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        nestedFieldName: 'enabled',
        value: newVal,
      })
    );
  };

  const updateSecret = (secret: IntegrationSecret | undefined) => {
    if (!secret) return;

    dispatch(
      tabActions.nodeInputUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        fieldName: 'secretName',
        value: secret.identifier,
      })
    );

    dispatch(
      tabActions.nodeInputUpdate({
        nodeId: command.node.id,
        fieldId: field.id,
        fieldName: 'secretUsage',
        value: secret.type,
      })
    );
  };

  return (
    <Container>
      <Stack spacing={1}>
        <Label title='Authentication' tooltip='Set to true if you want to send auth credentials with this request.' />
        <ToggleButtonGroup
          size='small'
          color={enabled ? 'primary' : undefined}
          value={enabled}
          exclusive
          onChange={(_, newVal) => newVal !== undefined && newVal !== null && toggleEnabled(newVal)}
          fullWidth
        >
          <ToggleButton size='small' value={true}>
            Auth
          </ToggleButton>
          <ToggleButton size='small' value={false}>
            No Auth
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {enabled ? (
        secrets.length === 0 ? (
          <Alert
            severity='warning'
            action={
              <Button onClick={() => navigate(`/${service_id}/secrets`)} color='inherit' small customColor='orange'>
                Create
              </Button>
            }
          >
            You do not have any secrets.
          </Alert>
        ) : (
          <Stack spacing={1}>
            <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
              <Label title='Secret' tooltip='Use a secret to authenticate this request securely. ' />
              {secretName && secretType && (
                <Chip
                  color={secretType === 'bearer' ? 'info' : 'primary'}
                  label={capitalize(secretType)}
                  size='small'
                  variant='outlined'
                  sx={{ fontSize: '1.3ch', height: '19px !important' }}
                />
              )}
            </Stack>
            <Secrets
              refetch={refetch}
              value={secretName}
              loading={isSecretsLoading}
              secrets={secrets}
              onChange={(_, item) => updateSecret(item?.value)}
              placeholder='Secret'
            />
          </Stack>
        )
      ) : null}
    </Container>
  );
};
